import React, { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import type { PlaceCoordinates } from '@/lib/tourOptimization';
import PlaceDetailPopup from './PlaceDetailPopup';

interface CityTour {
  id: string;
  title: string;
  description?: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  duration: string;
  style: string;
  museum_count: number;
  name_places?: any[];
  Latitude_places?: number[];
  Longitude_places?: number[];
}

interface CityTourMapProps {
  tours: CityTour[];
  highlightedTourId?: string;
  currentPageState?: any;
  onOpenPodcastGenerator?: (locationDetails: any) => void;
}

// Calculate geographic center of tour places
const calculateTourCenter = (tour: CityTour): [number, number] => {
  if (!tour.name_places || tour.name_places.length === 0 ||
      !tour.Latitude_places || !tour.Longitude_places ||
      tour.Latitude_places.length === 0 || tour.Longitude_places.length === 0) {
    // Fallback to original tour coordinates
    return [tour.longitude, tour.latitude];
  }

  const validCoordinates: Array<[number, number]> = [];
  
  for (let i = 0; i < tour.name_places.length; i++) {
    if (tour.Latitude_places[i] !== undefined && tour.Longitude_places[i] !== undefined) {
      validCoordinates.push([Number(tour.Longitude_places[i]), Number(tour.Latitude_places[i])]);
    }
  }

  if (validCoordinates.length === 0) {
    return [tour.longitude, tour.latitude];
  }

  // Calculate average longitude and latitude
  const avgLng = validCoordinates.reduce((sum, coord) => sum + coord[0], 0) / validCoordinates.length;
  const avgLat = validCoordinates.reduce((sum, coord) => sum + coord[1], 0) / validCoordinates.length;

  return [avgLng, avgLat];
};

const CityTourMap: React.FC<CityTourMapProps> = ({ tours, highlightedTourId, currentPageState, onOpenPodcastGenerator }) => {
  const navigate = useNavigate();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const userLocationMarker = useRef<mapboxgl.Marker | null>(null);
  const [accessToken, setAccessToken] = useState<string>('');
  const [currentZoom, setCurrentZoom] = useState<number>(2);
  const [selectedPlace, setSelectedPlace] = useState<{
    name: string;
    latitude: number;
    longitude: number;
  } | null>(null);
  const [selectedTourDetail, setSelectedTourDetail] = useState<CityTour | null>(null);

  // Keep a ref in sync to avoid stale closures in Mapbox event handlers
  const selectedPlaceRef = useRef<typeof selectedPlace>(null);
  useEffect(() => {
    selectedPlaceRef.current = selectedPlace;
    // Auto-close tour detail when a place detail popup opens
    if (selectedPlace) {
      setSelectedTourDetail(null);
    }
  }, [selectedPlace]);

  const dynamicLayerIds = useRef<Set<string>>(new Set());
  const dynamicSourceIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        if (error) throw error;
        setAccessToken(data.token);
      } catch (error) {
        console.error('Error fetching Mapbox token:', error);
      }
    };

    fetchMapboxToken();
  }, []);

  // Get initial map center based on user location
  const getInitialMapCenter = (): { center: [number, number]; zoom: number } => {
    // Try to get user location from geolocation or IP
    const tryGetUserLocation = async () => {
      if (navigator.geolocation) {
        return new Promise<{ lat: number; lng: number } | null>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            }),
            () => resolve(null),
            { enableHighAccuracy: false, timeout: 3000 }
          );
        });
      }
      return null;
    };

    // Default views by region
    const getRegionalView = (lat: number, lng: number): { center: [number, number]; zoom: number } => {
      // Europe
      if (lat >= 35 && lat <= 71 && lng >= -10 && lng <= 40) {
        return { center: [10, 54], zoom: 4 }; // Center on Europe
      }
      // North America
      else if (lat >= 24 && lat <= 72 && lng >= -168 && lng <= -52) {
        return { center: [-95, 50], zoom: 3 }; // Center on North America
      }
      // Asia
      else if (lat >= 10 && lat <= 70 && lng >= 60 && lng <= 180) {
        return { center: [105, 35], zoom: 3 }; // Center on Asia
      }
      // South America
      else if (lat >= -60 && lat <= 15 && lng >= -82 && lng <= -30) {
        return { center: [-60, -15], zoom: 3 }; // Center on South America
      }
      // Africa
      else if (lat >= -35 && lat <= 37 && lng >= -20 && lng <= 55) {
        return { center: [20, 0], zoom: 3 }; // Center on Africa
      }
      // Australia/Oceania
      else if (lat >= -50 && lat <= -10 && lng >= 110 && lng <= 180) {
        return { center: [135, -25], zoom: 4 }; // Center on Australia
      }
      // Default to Europe (Paris region)
      else {
        return { center: [2.3522, 48.8566], zoom: 4 };
      }
    };

    // Check if we have stored user location from parent component
    const storedLocation = (window as any).__userLocation;
    if (storedLocation) {
      return getRegionalView(storedLocation.lat, storedLocation.lng);
    }
    
    // Default to Europe view
    return { center: [2.3522, 48.8566], zoom: 4 };
  };

  useEffect(() => {
    if (!accessToken || !mapContainer.current) return;

    mapboxgl.accessToken = accessToken;

    const { center, zoom } = getInitialMapCenter();

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom,
      projection: 'globe' as any,
    });

    // Add atmosphere
    map.current.on('style.load', () => {
      map.current?.setFog({
        color: 'rgb(186, 210, 235)',
        'high-color': 'rgb(36, 92, 223)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6,
      });
    });

    // Ensure layers are added only after map load
    map.current.on('load', () => {
      updateMap();
      updateTourDisplay(map.current!.getZoom());

      // Expose map view getters/setters for Tours page state persistence
      (window as any).getCityTourMapView = () => {
        if (!map.current) return null;
        const c = map.current.getCenter();
        return { center: { lat: c.lat, lng: c.lng }, zoom: map.current.getZoom() };
      };
      (window as any).setCityTourMapView = (center: { lat: number; lng: number }, zoom?: number) => {
        if (!map.current || !center) return;
        map.current.jumpTo({ center: [center.lng, center.lat], zoom: zoom ?? map.current.getZoom() });
      };
    });

    // Handle zoom and move changes
    map.current.on('zoom', () => {
      if (map.current) {
        const newZoom = map.current.getZoom();
        setCurrentZoom(newZoom);
        // Update tour display based on zoom level
        updateTourDisplay(newZoom);
      }
    });

    map.current.on('moveend', () => {
      updateMapCenterForCarousel();
    });

    // Listen for location centering events from location button
    const handleCenterMapOnLocation = (event: CustomEvent) => {
      const { lat, lng } = event.detail;
      if (map.current) {
        map.current.flyTo({
          center: [lng, lat],
          zoom: 14,
          duration: 1500
        });
        // Update user location marker
        addUserLocationMarker(lat, lng);
      }
    };

    document.addEventListener('centerMapOnLocation', handleCenterMapOnLocation as EventListener);
    
    return () => {
      document.removeEventListener('centerMapOnLocation', handleCenterMapOnLocation as EventListener);
      clearMarkers();
      if (userLocationMarker.current) {
        userLocationMarker.current.remove();
        userLocationMarker.current = null;
      }
      map.current?.remove();
    };
  }, [accessToken]);

  // Update tour display based on zoom level (11+ = detailed view with routes)
  const updateTourDisplay = (zoom: number) => {
    if (!map.current) return;
    
    const detailZoomThreshold = 11; // Approximately 1cm = 2km
    
    // Show/hide city markers based on zoom - hide when detailed tours appear
    markers.current.forEach(marker => {
      const element = marker.getElement();
      if (element.classList.contains('city-tour-marker')) {
        // Hide city markers when detailed view appears (zoom >= 11)
        element.style.display = zoom >= 11 ? 'none' : 'block';
      }
    });

    // Update layer visibility for routes and place details
    dynamicLayerIds.current.forEach(layerId => {
      if (map.current!.getLayer(layerId)) {
        if (layerId.includes('route-layer') || layerId.includes('places-circle') || layerId.includes('places-number')) {
          map.current!.setLayoutProperty(layerId, 'visibility', 
            zoom >= detailZoomThreshold ? 'visible' : 'none'
          );
        }
      }
    });
  };

  // Add user location marker
  const addUserLocationMarker = useCallback((lat: number, lng: number) => {
    if (!map.current) return;
    
    // Remove existing marker if any
    if (userLocationMarker.current) {
      userLocationMarker.current.remove();
    }
    
    // Create user location marker element (blue dot with light blue halo)
    const el = document.createElement('div');
    el.className = 'user-location-marker';
    el.style.cssText = `
      width: 20px;
      height: 20px;
      position: relative;
    `;
    
    // Outer light blue circle (halo)
    const outerCircle = document.createElement('div');
    outerCircle.style.cssText = `
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: rgba(66, 133, 244, 0.2);
      border: 1px solid rgba(66, 133, 244, 0.4);
    `;
    
    // Inner darker blue dot
    const innerDot = document.createElement('div');
    innerDot.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #2563eb;
      border: 2px solid white;
      box-shadow: 0 0 3px rgba(0, 0, 0, 0.3);
    `;
    
    el.appendChild(outerCircle);
    el.appendChild(innerDot);
    
    // Create and add marker
    userLocationMarker.current = new mapboxgl.Marker({
      element: el,
      anchor: 'center'
    })
      .setLngLat([lng, lat])
      .addTo(map.current);
  }, []);

  const clearMarkers = () => {
    markers.current.forEach(m => m.remove());
    markers.current = [];

    if (map.current) {
      // Remove dynamically added layers
      dynamicLayerIds.current.forEach((layerId) => {
        if (map.current && map.current.getLayer(layerId)) {
          try { map.current.removeLayer(layerId); } catch {}
        }
      });
      dynamicLayerIds.current.clear();

      // Remove dynamically added sources
      dynamicSourceIds.current.forEach((sourceId) => {
        if (map.current && map.current.getSource(sourceId)) {
          try { map.current.removeSource(sourceId); } catch {}
        }
      });
      dynamicSourceIds.current.clear();

      // Safety: remove any leftover tour layers by prefix
      try {
        const style = map.current.getStyle();
        const layers = style?.layers || [];
        layers.forEach(l => {
          if (l.id.startsWith('tour-route-layer-') || l.id.startsWith('tour-places-circle-')) {
            if (map.current!.getLayer(l.id)) map.current!.removeLayer(l.id);
          }
        });
        const sources = Object.keys(style?.sources || {});
        sources.forEach(id => {
          if (id.startsWith('tour-route-') || id.startsWith('tour-places-')) {
            if (map.current!.getSource(id)) map.current!.removeSource(id);
          }
        });
      } catch {}
    }
  };

  // Create simple tour markers without clustering (fixed, no hover animation)
  const createTourMarker = (tour: CityTour) => {
    const color = getStyleColor(tour.style);
    
    const markerEl = document.createElement('div');
    markerEl.className = 'city-tour-marker';
    markerEl.style.cssText = `
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: ${color};
      border: 2px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.25);
      cursor: pointer;
      transform: none;
      transition: none;
      will-change: auto;
    `;

    // Click to focus on tour (no hover effects to avoid jitter)
    markerEl.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const tourSelectEvent = new CustomEvent('tourMarkerClick', {
        detail: { tour }
      });
      document.dispatchEvent(tourSelectEvent);
      
      if (map.current) {
        const [centerLng, centerLat] = calculateTourCenter(tour);
        map.current.easeTo({
          center: [centerLng, centerLat],
          zoom: 12,
          duration: 1000
        });
      }
    });

    // Use calculated center instead of original coordinates
    const [centerLng, centerLat] = calculateTourCenter(tour);
    return new mapboxgl.Marker({ element: markerEl, anchor: 'center' })
      .setLngLat([centerLng, centerLat]);
  };

  // Create simple tour route without optimization for instant display
  const createTourRoute = (tour: CityTour): PlaceCoordinates[] => {
    if (!tour.name_places || tour.name_places.length === 0) return [];

    const coordinates: PlaceCoordinates[] = [];

    // All points are now already cleaned in the database
    for (let i = 0; i < tour.name_places.length; i++) {
      const placeName = tour.name_places[i];
      
      if (tour.Latitude_places?.[i] !== undefined && tour.Longitude_places?.[i] !== undefined) {
        coordinates.push({
          name: placeName,
          latitude: Number(tour.Latitude_places[i]),
          longitude: Number(tour.Longitude_places[i]),
          hasPodcast: false // We'll skip podcast checking for instant display
        });
      }
    }

    // Return coordinates in original order for instant display
    return coordinates;
  };

  const addDetailedTourToMap = (tour: CityTour, tourIndex: number) => {
    const coordinates = createTourRoute(tour);
    if (coordinates.length === 0) return;

    const color = getStyleColor(tour.style);

    // Add detailed place points as a Mapbox layer (fixed to map)
    const placesSourceId = `tour-places-${tourIndex}`;
    const placesCircleLayerId = `tour-places-circle-${tourIndex}`;

    // Defensive cleanup
    if (map.current!.getLayer(placesCircleLayerId)) map.current!.removeLayer(placesCircleLayerId);
    if (map.current!.getSource(placesSourceId)) map.current!.removeSource(placesSourceId);

    if (coordinates.length > 0) {
      const placesGeojson = {
        type: 'FeatureCollection',
        features: coordinates.map((place, index) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [place.longitude, place.latitude]
          },
          properties: {
            index: index + 1,
            name: place.name,
            tourTitle: tour.title
          }
        }))
      } as any;

      map.current!.addSource(placesSourceId, { type: 'geojson', data: placesGeojson });
      dynamicSourceIds.current.add(placesSourceId);

      // Add place circles
      map.current!.addLayer({
        id: placesCircleLayerId,
        type: 'circle',
        source: placesSourceId,
        paint: {
          'circle-color': color,
          'circle-radius': 8,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        },
        layout: {
          'visibility': currentZoom >= 11 ? 'visible' : 'none'
        }
      });
      dynamicLayerIds.current.add(placesCircleLayerId);

    // Add click events for place details popup
    map.current!.on('click', placesCircleLayerId, (e) => {
      if (e.features && e.features[0]) {
        const feature = e.features[0];
        const coordinates = (feature.geometry as any).coordinates.slice() as [number, number];
        const properties = feature.properties;
        
        // Center on place with slight zoom
        if (map.current) {
          const currentZoom = map.current.getZoom();
          const targetZoom = Math.max(currentZoom, 12); // Slight zoom-in only
          
          map.current.easeTo({
            center: coordinates,
            zoom: targetZoom,
            duration: 800
          });
        }
        
        setSelectedPlace({
          name: properties!.name,
          latitude: coordinates[1],
          longitude: coordinates[0]
        });
      }
    });

      // Change cursor on hover over places
      map.current!.on('mouseenter', placesCircleLayerId, () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current!.on('mouseleave', placesCircleLayerId, () => {
        if (map.current) map.current.getCanvas().style.cursor = '';
      });
    }

    // Add route line
    if (coordinates.length > 1) {
      const routeCoordinates = coordinates.map(place => [place.longitude, place.latitude]);
      
      const sourceId = `tour-route-${tourIndex}`;
      const layerId = `tour-route-layer-${tourIndex}`;
      const clickLayerId = `tour-route-click-${tourIndex}`;

      if (!map.current!.getSource(sourceId)) {
        map.current!.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              tourId: tour.id,
              tourTitle: tour.title
            },
            geometry: {
              type: 'LineString',
              coordinates: routeCoordinates
            }
          }
        });
        dynamicSourceIds.current.add(sourceId);

        // Add invisible wider line underneath for easier clicking
        map.current!.addLayer({
          id: clickLayerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'visibility': currentZoom >= 11 ? 'visible' : 'none'
          },
          paint: {
            'line-color': color,
            'line-width': 12, // Much wider for easier clicking
            'line-opacity': 0 // Invisible but clickable
          }
        });
        dynamicLayerIds.current.add(clickLayerId);

        // Add visible line on top - Place before circles to keep points visible on top
        map.current!.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'visibility': currentZoom >= 11 ? 'visible' : 'none'
          },
          paint: {
            'line-color': color,
            'line-width': 3,
            'line-opacity': 0.8
          }
        }, placesCircleLayerId); // Place line BEFORE circles to keep points on top
        dynamicLayerIds.current.add(layerId);

        // Click handler for both lines - select tour only
        const handleRouteClick = (e: any) => {
          e.preventDefault();
          
          // Send event to select this tour in the carousel without opening popup
          const tourSelectEvent = new CustomEvent('tourMarkerClick', {
            detail: { tour }
          });
          document.dispatchEvent(tourSelectEvent);
        };

        map.current!.on('click', layerId, handleRouteClick);
        map.current!.on('click', clickLayerId, handleRouteClick);

        // Change cursor on hover for both lines
        const handleMouseEnter = () => {
          if (map.current) map.current.getCanvas().style.cursor = 'pointer';
        };
        const handleMouseLeave = () => {
          if (map.current) map.current.getCanvas().style.cursor = '';
        };

        map.current!.on('mouseenter', layerId, handleMouseEnter);
        map.current!.on('mouseleave', layerId, handleMouseLeave);
        map.current!.on('mouseenter', clickLayerId, handleMouseEnter);
        map.current!.on('mouseleave', clickLayerId, handleMouseLeave);
      }
    }
  };

  const updateMapWithHighlight = async (highlightedTourId: string, allTours: CityTour[]) => {
    if (!map.current) return;
    
    clearMarkers();
    
    // Add all tours with appropriate opacity
    for (let i = 0; i < allTours.length; i++) {
      const tour = allTours[i];
      const isHighlighted = tour.id === highlightedTourId;
      const opacity = isHighlighted ? 1.0 : 0.3;
      
      await addDetailedTourToMapWithOpacity(tour, i, opacity, isHighlighted);
    }
  };

  const addDetailedTourToMapWithOpacity = async (tour: CityTour, tourIndex: number, opacity: number, isHighlighted: boolean) => {
    const coordinates = await createTourRoute(tour);
    if (coordinates.length === 0) return;

    const color = getStyleColor(tour.style);

    // Only create city marker if not already exists for this tour
    const [centerLng, centerLat] = calculateTourCenter(tour);
    const existingMarkerIndex = markers.current.findIndex(m => {
      const markerPos = m.getLngLat();
      return Math.abs(markerPos.lng - centerLng) < 0.0001 && 
             Math.abs(markerPos.lat - centerLat) < 0.0001;
    });
    
    if (existingMarkerIndex === -1) {
      // Add city center marker (no numbers) - use createTourMarker for consistency
      const cityMarker = createTourMarker(tour);
      cityMarker.addTo(map.current!);
      markers.current.push(cityMarker);
    }

    // Add detailed place points as a Mapbox layer
    const placesSourceId = `tour-places-${tourIndex}`;
    const placesCircleLayerId = `tour-places-circle-${tourIndex}`;
    const placesNumberLayerId = `tour-places-number-${tourIndex}`;

    // Defensive cleanup
    if (map.current!.getLayer(placesCircleLayerId)) map.current!.removeLayer(placesCircleLayerId);
    if (map.current!.getLayer(placesNumberLayerId)) map.current!.removeLayer(placesNumberLayerId);
    if (map.current!.getSource(placesSourceId)) map.current!.removeSource(placesSourceId);

    const placesGeojson = {
      type: 'FeatureCollection',
      features: coordinates.map((place, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [place.longitude, place.latitude]
        },
        properties: {
          index: index + 1,
          name: place.name,
          tourTitle: tour.title,
          hasPodcast: place.hasPodcast || false
        }
      }))
    } as any;

    map.current!.addSource(placesSourceId, { type: 'geojson', data: placesGeojson });
    dynamicSourceIds.current.add(placesSourceId);

    // Use different circle sizes based on podcast availability
    // Higher z-index for highlighted tour to show on top of other tours
    map.current!.addLayer({
      id: placesCircleLayerId,
      type: 'circle',
      source: placesSourceId,
      paint: {
        'circle-color': color,
        'circle-radius': [
          'case',
          ['get', 'hasPodcast'],
          isHighlighted ? 14 : 12,
          isHighlighted ? 8 : 7
        ],
        'circle-stroke-width': 3,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': isHighlighted ? 0.95 : 0.5,
        'circle-stroke-opacity': isHighlighted ? 1 : 0.6
      },
      minzoom: 6
    }, isHighlighted ? undefined : 'waterway-label'); // Insert highlighted tour layers on top
    dynamicLayerIds.current.add(placesCircleLayerId);

    // Add number labels on top of circles - ONLY for highlighted tour
    if (isHighlighted) {
      map.current!.addLayer({
        id: placesNumberLayerId,
        type: 'symbol',
        source: placesSourceId,
        layout: {
          'text-field': ['get', 'index'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 16,
          'text-allow-overlap': true,
          'text-ignore-placement': true
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': color,
          'text-halo-width': 2,
          'text-opacity': 1
        },
        minzoom: 6
      }); // Don't specify beforeId to place on top of everything
      dynamicLayerIds.current.add(placesNumberLayerId);
    }

    // Add click events for place details popup
    map.current!.on('click', placesCircleLayerId, (e) => {
      if (e.features && e.features[0]) {
        const feature = e.features[0];
        const coordinates = (feature.geometry as any).coordinates.slice() as [number, number];
        const properties = feature.properties;
        
        // Center on place with slight zoom
        if (map.current) {
          const currentZoom = map.current.getZoom();
          const targetZoom = Math.max(currentZoom, 14); // Ensure minimum zoom of 14
          
          map.current.easeTo({
            center: coordinates,
            zoom: targetZoom,
            duration: 800
          });
        }
        
        setSelectedPlace({
          name: properties!.name,
          latitude: coordinates[1],
          longitude: coordinates[0]
        });
      }
    });

    // Change cursor on hover over places
    map.current!.on('mouseenter', placesCircleLayerId, () => {
      if (map.current) map.current.getCanvas().style.cursor = 'pointer';
    });
    map.current!.on('mouseleave', placesCircleLayerId, () => {
      if (map.current) map.current.getCanvas().style.cursor = '';
    });

    // Add route line
    if (coordinates.length > 1) {
      const routeCoordinates = coordinates.map(place => [place.longitude, place.latitude]);
      
      const sourceId = `tour-route-${tourIndex}`;
      const layerId = `tour-route-layer-${tourIndex}`;

      if (!map.current!.getSource(sourceId)) {
        map.current!.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {
              tourId: tour.id,
              tourTitle: tour.title
            },
            geometry: {
              type: 'LineString',
              coordinates: routeCoordinates
            }
          }
        });
        dynamicSourceIds.current.add(sourceId);

        // Add invisible wider line underneath for easier clicking (same color, transparent)
        const clickLayerId = `tour-route-click-${tourIndex}`;
        map.current!.addLayer({
          id: clickLayerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': color,
            'line-width': 12, // Much wider for easier clicking
            'line-opacity': 0 // Invisible but clickable
          }
        }, layerId); // Place under the visible line
        dynamicLayerIds.current.add(clickLayerId);

        // Add visible line on top - Place before places circle layer to ensure points stay on top
        map.current!.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': color,
            'line-width': isHighlighted ? 4 : 2,
            'line-opacity': isHighlighted ? 0.9 : 0.3
          }
        }, placesCircleLayerId); // Place route line BEFORE circles to keep points on top
        dynamicLayerIds.current.add(layerId);

        // Click handler for both visible and invisible lines - select tour only
        const handleRouteClick = (e: any) => {
          e.preventDefault();
          
          // Send event to select this tour in the carousel without opening popup
          const tourSelectEvent = new CustomEvent('tourMarkerClick', {
            detail: { tour }
          });
          document.dispatchEvent(tourSelectEvent);
        };

        map.current!.on('click', layerId, handleRouteClick);
        map.current!.on('click', clickLayerId, handleRouteClick);

        // Change cursor on hover for both lines
        const handleMouseEnter = () => {
          if (map.current) map.current.getCanvas().style.cursor = 'pointer';
        };
        const handleMouseLeave = () => {
          if (map.current) map.current.getCanvas().style.cursor = '';
        };

        map.current!.on('mouseenter', layerId, handleMouseEnter);
        map.current!.on('mouseleave', layerId, handleMouseLeave);
        map.current!.on('mouseenter', clickLayerId, handleMouseEnter);
        map.current!.on('mouseleave', clickLayerId, handleMouseLeave);
      }
    }
  };

  const updateMapCenterForCarousel = useCallback(() => {
    if (!map.current) return;
    
    const center = map.current.getCenter();
    const zoom = map.current.getZoom();
    
    // Send event to update carousel with map center and zoom
    const mapCenterEvent = new CustomEvent('mapCenterChange', {
      detail: { 
        center: { lat: center.lat, lng: center.lng },
        zoom: zoom
      }
    });
    document.dispatchEvent(mapCenterEvent);
  }, []);

  // Listen for zoom changes to trigger carousel updates
  useEffect(() => {
    if (!map.current) return;

    const handleZoomEnd = () => {
      updateMapCenterForCarousel();
    };

    map.current.on('zoomend', handleZoomEnd);
    map.current.on('moveend', handleZoomEnd);
    
    return () => {
      if (map.current) {
        map.current.off('zoomend', handleZoomEnd);
        map.current.off('moveend', handleZoomEnd);
      }
    };
  }, [updateMapCenterForCarousel]);

  const updateMap = useCallback(() => {
    if (!map.current || tours.length === 0) return;

    clearMarkers();

    // Check if we should highlight only one tour based on zoom and position
    const zoom = map.current.getZoom();
    const center = map.current.getCenter();
    
    if (highlightedTourId && zoom > 10) {
      // When zoomed in and we have a highlighted tour, show all tours but highlight the selected one
      updateMapWithHighlight(highlightedTourId, tours);
    } else {
      // Show all tour markers normally
      tours.forEach((tour, index) => {
        if (tour.latitude && tour.longitude) {
          // Create simple tour marker
          const marker = createTourMarker(tour);
          marker.addTo(map.current!);
          markers.current.push(marker);

          // Add detailed tour routes and places for instant display
          addDetailedTourToMap(tour, index);
        }
      });
    }
  }, [tours, highlightedTourId]);

  const getStyleColor = (style: string) => {
    switch (style) {
      case 'must-do': return '#ef4444';
      case 'best-rated': return '#10b981';
      case 'off-the-beaten-path': return '#3b82f6';
      default: return '#8b5cf6';
    }
  };

  const getStyleLabel = (style: string) => {
    switch (style) {
      case 'must-do': return 'Must Do';
      case 'best-rated': return 'Mieux Notés';
      case 'off-the-beaten-path': return 'Hors des Sentiers';
      default: return style;
    }
  };

  useEffect(() => {
    updateMap();
    // Also update map center when map updates
    setTimeout(() => updateMapCenterForCarousel(), 100);
  }, [updateMap, updateMapCenterForCarousel]);

  // Add user location marker when available
  useEffect(() => {
    const storedLocation = (window as any).__userLocation;
    if (storedLocation && map.current && accessToken) {
      addUserLocationMarker(storedLocation.lat, storedLocation.lng);
    }
  }, [accessToken, addUserLocationMarker]);

  // Listen for tour focus events from TourDetailView and carousel
  useEffect(() => {
    const handleTourMapFocus = async (event: CustomEvent) => {
      const { tour, focusOnPlaces, highlightTour, allTours } = event.detail;
      
      if (!map.current || !tour) return;

      // Handle highlight tour (from carousel)
      if (highlightTour && allTours) {
        // Center map on selected tour using calculated center
        const [centerLng, centerLat] = calculateTourCenter(tour);
        map.current.easeTo({
          center: [centerLng, centerLat],
          zoom: 13.5,
          duration: 1200,
          easing: (t) => t * (2 - t) // Smooth easing
        });
        return;
      }

      if (focusOnPlaces && tour.name_places && tour.name_places.length > 0) {
        // Focus on tour places
        const coordinates: Array<[number, number]> = [];
        
        // Collect coordinates from tour places
        for (let i = 0; i < tour.name_places.length; i++) {
          if (tour.Latitude_places?.[i] !== undefined && tour.Longitude_places?.[i] !== undefined) {
            coordinates.push([Number(tour.Longitude_places[i]), Number(tour.Latitude_places[i])]);
          } else {
            // Fallback: try to get coordinates from podcasts table
            try {
              const { data } = await supabase
                .from('podcasts')
                .select('Latitude, Longitude')
                .eq('Name', tour.name_places[i])
                .single();
              
              if (data && data.Latitude && data.Longitude) {
                coordinates.push([data.Longitude, data.Latitude]);
              }
            } catch (error) {
              console.warn(`Could not fetch coordinates for ${tour.name_places[i]}`);
            }
          }
        }
        
        if (coordinates.length > 0) {
          // Calculate bounds to fit all places
          const bounds = coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
          
          // Fit map to show all tour places with padding
          map.current.fitBounds(bounds, {
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            maxZoom: 15,
            duration: 1000
          });
        } else {
          // Fallback to calculated tour center
          const [centerLng, centerLat] = calculateTourCenter(tour);
          map.current.easeTo({
            center: [centerLng, centerLat],
            zoom: 14,
            duration: 1000
          });
        }
      } else {
        // Default behavior - zoom to calculated tour center
        const [centerLng, centerLat] = calculateTourCenter(tour);
        map.current.easeTo({
          center: [centerLng, centerLat],
          zoom: 14,
          duration: 1000
        });
      }
    };

    document.addEventListener('tourMapFocus', handleTourMapFocus as EventListener);
    
    return () => {
      document.removeEventListener('tourMapFocus', handleTourMapFocus as EventListener);
    };
  }, []);

  if (!accessToken) {
    return (
      <div className="w-full h-[600px] bg-muted rounded-lg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement de la carte...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden shadow-lg">
      <div ref={mapContainer} className="absolute inset-0" />
      
      {/* Place Detail Popup */}
      {selectedPlace && (
        <PlaceDetailPopup
          placeName={selectedPlace.name}
          latitude={selectedPlace.latitude}
          longitude={selectedPlace.longitude}
          onClose={() => setSelectedPlace(null)}
          currentPageState={currentPageState}
          onOpenPodcastGenerator={onOpenPodcastGenerator}
        />
      )}

      {/* Tour Detail Popup */}
      {selectedTourDetail && (
        <div className="absolute inset-x-4 top-4 z-[9999] pointer-events-auto bg-card border rounded-lg shadow-lg w-[calc(100%-2rem)] max-w-sm sm:max-w-md md:max-w-lg mx-auto max-h-[70vh] sm:max-h-[60vh] overflow-hidden">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">{selectedTourDetail.title}</h3>
              <button
                onClick={() => setSelectedTourDetail(null)}
                className="text-muted-foreground hover:text-foreground w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted/50 transition-colors"
                aria-label="Fermer les détails du tour"
              >
                ✕
              </button>
            </div>
            <div className="space-y-2 text-sm pr-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none] max-h-[46vh] sm:max-h-[40vh]">
              <p><span className="font-medium">Ville:</span> {selectedTourDetail.city}</p>
              <p><span className="font-medium">Durée:</span> {selectedTourDetail.duration}</p>
              <p><span className="font-medium">Nombre de lieux:</span> {selectedTourDetail.museum_count}</p>
              {selectedTourDetail.description && (
                <p><span className="font-medium">Description:</span> {selectedTourDetail.description}</p>
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={() => {
                  const [centerLng, centerLat] = calculateTourCenter(selectedTourDetail);
                  if (map.current) {
                    map.current.easeTo({
                      center: [centerLng, centerLat],
                      zoom: 12,
                      duration: 1000
                    });
                  }
                  setSelectedTourDetail(null);
                }}
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg font-medium min-h-[48px] transition-colors"
                aria-label={`Voir le tour ${selectedTourDetail.title} sur la carte`}
              >
                Voir sur la carte
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityTourMap;